import { useState, useEffect, useRef, useMemo } from 'react';
import api from '@/lib/api';
import { Plus, Trash2, Download, Upload, FileSpreadsheet, Shield, AlertTriangle, CheckCircle2, Calendar, Search } from 'lucide-react';

// Indian Trade Marks Registry: renewal notices go out 1 year before expiry;
// the law permits renewal up to 6 months after expiry with a late-fee,
// so our three tiers match the operational reality.
const CRITICAL_DAYS = 90;   // < 90 days or already expired → red, must act NOW
const WARN_DAYS = 365;       // 90 – 365 days → amber, plan the renewal

const statusColor = (s) => {
  if (s === 'Registered') return '#2D8B5E';
  if (s === 'Pending' || s === 'Filed' || (s && s.toLowerCase().includes('formalities'))) return '#BA7517';
  if (s === 'Expired' || s === 'Removed') return '#D4726A';
  if (s === 'Opposed') return '#DC2626';
  if (s === 'Refused' || s === 'Abandoned') return '#991B1B';
  return '#6B7280';
};

function daysTo(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  const diff = Math.round((d - new Date()) / (1000 * 60 * 60 * 24));
  return diff;
}

function renewalState(dateStr) {
  const d = daysTo(dateStr);
  if (d == null) return { tier: 'unknown', days: null, label: '—', bg: '#F3F4F6', fg: '#6B7280' };
  if (d < 0) return { tier: 'expired', days: d, label: `Expired ${Math.abs(d)}d ago`, bg: '#FEE2E2', fg: '#991B1B' };
  if (d <= CRITICAL_DAYS) return { tier: 'critical', days: d, label: `Renew in ${d}d`, bg: '#FEE2E2', fg: '#991B1B' };
  if (d <= WARN_DAYS) return { tier: 'warn', days: d, label: `Renew in ${Math.round(d / 30)}mo`, bg: '#FEF3C7', fg: '#92400E' };
  const years = (d / 365).toFixed(1).replace('.0', '');
  return { tier: 'ok', days: d, label: `${years}y remaining`, bg: '#DCFCE7', fg: '#166534' };
}

export default function IPRegister() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const fileRef = useRef(null);
  const emptyForm = { mark_name: '', type: 'Trademark', registration_status: 'Registered', registration_number: '', class_info: '', entity: '', expiry_date: '', story: '', notes: '' };
  const [form, setForm] = useState(emptyForm);

  const refresh = async () => { const r = await api.get('/ip-register'); setItems(r.data); };
  useEffect(() => { refresh().finally(() => setLoading(false)); }, []);

  const resetForm = () => { setForm(emptyForm); setEditId(null); setShowForm(false); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editId) await api.put(`/ip-register/${editId}`, form);
    else await api.post('/ip-register', form);
    resetForm(); refresh();
  };

  const handleEdit = (i) => {
    setForm({
      mark_name: i.mark_name || '', type: i.type || 'Trademark', registration_status: i.registration_status || '',
      registration_number: i.registration_number || '', class_info: i.class_info || '', entity: i.entity || '',
      expiry_date: i.expiry_date || '', story: i.story || '', notes: i.notes || '',
    });
    setEditId(i.id); setShowForm(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try { const r = await api.post('/ip-register/upload-csv', fd); alert(`Imported ${r.data.imported} items`); refresh(); }
    catch { alert('Upload failed'); }
    e.target.value = '';
  };

  const handleDownload = async (template = false) => {
    const url = template ? '/ip-register/csv-template' : '/ip-register/download-csv';
    const r = await api.get(url, { responseType: 'blob' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([r.data]));
    a.download = template ? 'ip_template.csv' : 'ip_register.csv'; a.click();
  };

  // Enriched items with renewal tier
  const enriched = useMemo(
    () => items.map(i => ({ ...i, renewal: renewalState(i.expiry_date) })),
    [items]
  );

  const summary = useMemo(() => ({
    total: enriched.length,
    registered: enriched.filter(i => i.registration_status === 'Registered').length,
    pending: enriched.filter(i => ['Pending', 'Filed'].includes(i.registration_status) || (i.registration_status || '').toLowerCase().includes('formalities')).length,
    issues: enriched.filter(i => ['Opposed', 'Refused', 'Abandoned', 'Removed'].includes(i.registration_status)).length,
    expired: enriched.filter(i => i.renewal.tier === 'expired').length,
    critical: enriched.filter(i => i.renewal.tier === 'critical').length,
    warn: enriched.filter(i => i.renewal.tier === 'warn').length,
  }), [enriched]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return enriched
      .filter(i => {
        if (filter === 'critical' && !['critical', 'expired'].includes(i.renewal.tier)) return false;
        if (filter === 'warn' && i.renewal.tier !== 'warn') return false;
        if (filter === 'registered' && i.registration_status !== 'Registered') return false;
        if (filter === 'pending' && !['Pending', 'Filed'].includes(i.registration_status) && !(i.registration_status || '').toLowerCase().includes('formalities')) return false;
        if (filter === 'issues' && !['Opposed', 'Refused', 'Abandoned', 'Removed'].includes(i.registration_status)) return false;
        if (q) {
          const hay = `${i.mark_name} ${i.registration_number} ${i.class_info} ${i.entity} ${i.type} ${i.registration_status}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      })
      // Sort: soonest-to-expire first (urgency wins), then by mark name.
      .sort((a, b) => {
        const da = a.renewal.days == null ? 9e9 : a.renewal.days;
        const dbx = b.renewal.days == null ? 9e9 : b.renewal.days;
        if (da !== dbx) return da - dbx;
        return (a.mark_name || '').localeCompare(b.mark_name || '');
      });
  }, [enriched, filter, search]);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-[#2A7B88] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-5 fade-in" data-testid="ip-register">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Shield size={18} style={{ color: '#1B2A4A' }} />
          <h2 className="text-lg font-bold" style={{ color: '#1B2A4A' }}>IP Register</h2>
          <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: '#F7F5F2', color: '#6B7280' }}>{summary.total} marks</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => handleDownload(true)} className="flex items-center gap-1 px-2 py-1 rounded text-xs border hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="ip-template-btn"><FileSpreadsheet size={12} /> Template</button>
          <button onClick={() => handleDownload(false)} className="flex items-center gap-1 px-2 py-1 rounded text-xs border hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="ip-export-btn"><Download size={12} /> Export</button>
          <button onClick={() => fileRef.current?.click()} className="flex items-center gap-1 px-2 py-1 rounded text-xs border hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#6B7280' }} data-testid="ip-import-btn"><Upload size={12} /> Import</button>
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleUpload} />
          <button onClick={() => { resetForm(); setShowForm(!showForm); }} className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="add-ip-btn"><Plus size={13} /> Add IP</button>
        </div>
      </div>

      {/* Alert strip — only renders when something actually needs attention */}
      {(summary.expired + summary.critical + summary.warn) > 0 && (
        <div className="rounded-lg border p-3" style={{ borderColor: '#FECACA', backgroundColor: '#FEF2F2' }} data-testid="ip-alerts-strip">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={14} style={{ color: '#991B1B' }} />
            <p className="text-sm font-semibold" style={{ color: '#991B1B' }}>Renewal attention needed</p>
          </div>
          <div className="flex flex-wrap gap-3 text-xs">
            {summary.expired > 0 && (
              <div><span className="font-bold" style={{ color: '#991B1B' }}>{summary.expired}</span> <span style={{ color: '#6B7280' }}>already expired</span></div>
            )}
            {summary.critical > 0 && (
              <div><span className="font-bold" style={{ color: '#991B1B' }}>{summary.critical}</span> <span style={{ color: '#6B7280' }}>due within {CRITICAL_DAYS} days</span></div>
            )}
            {summary.warn > 0 && (
              <div><span className="font-bold" style={{ color: '#92400E' }}>{summary.warn}</span> <span style={{ color: '#6B7280' }}>due within 1 year</span></div>
            )}
          </div>
        </div>
      )}

      {/* Stat pills / filter tabs */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="ip-stats">
        <StatPill label="All" value={summary.total} active={filter === 'all'} onClick={() => setFilter('all')} testId="filter-all" />
        <StatPill label="Registered" value={summary.registered} active={filter === 'registered'} onClick={() => setFilter('registered')} accent="#2D8B5E" testId="filter-registered" />
        <StatPill label="Pending" value={summary.pending} active={filter === 'pending'} onClick={() => setFilter('pending')} accent="#BA7517" testId="filter-pending" />
        <StatPill label="Issues" value={summary.issues} active={filter === 'issues'} onClick={() => setFilter('issues')} accent="#991B1B" testId="filter-issues" />
        <StatPill label={`<${CRITICAL_DAYS}d`} value={summary.critical + summary.expired} active={filter === 'critical'} onClick={() => setFilter('critical')} accent="#991B1B" testId="filter-critical" />
        <StatPill label="<1 year" value={summary.warn} active={filter === 'warn'} onClick={() => setFilter('warn')} accent="#92400E" testId="filter-warn" />
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#9CA3AF' }} />
        <input
          placeholder="Search mark / class / entity…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2 rounded-md border text-sm"
          style={{ borderColor: '#E5E1DC' }}
          data-testid="ip-search"
        />
      </div>

      {/* Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-lg border p-4 space-y-3" style={{ borderColor: '#E5E1DC' }} data-testid="ip-form">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input value={form.mark_name} onChange={e => setForm(p => ({...p, mark_name: e.target.value}))} placeholder="Mark name *" required className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
            <select value={form.type} onChange={e => setForm(p => ({...p, type: e.target.value}))} className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}>
              <option value="Trademark">Trademark</option><option value="Logo">Logo</option><option value="Domain">Domain</option><option value="Patent">Patent</option><option value="Copyright">Copyright</option>
            </select>
            <select value={form.registration_status} onChange={e => setForm(p => ({...p, registration_status: e.target.value}))} className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}>
              <option value="">Status…</option><option value="Registered">Registered</option><option value="Pending">Pending</option><option value="Filed">Filed</option><option value="Expired">Expired</option>
            </select>
            <input value={form.registration_number} onChange={e => setForm(p => ({...p, registration_number: e.target.value}))} placeholder="Registration / Application No." className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
            <input value={form.class_info} onChange={e => setForm(p => ({...p, class_info: e.target.value}))} placeholder="Class (e.g. Class 43)" className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
            <input value={form.entity} onChange={e => setForm(p => ({...p, entity: e.target.value}))} placeholder="Entity" className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
            <input type="date" value={form.expiry_date} onChange={e => setForm(p => ({...p, expiry_date: e.target.value}))} placeholder="Valid upto" className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="ip-expiry" />
          </div>
          <textarea value={form.story} onChange={e => setForm(p => ({...p, story: e.target.value}))} placeholder="Story behind this mark" rows={2} className="w-full px-3 py-2 rounded-md border text-sm resize-none" style={{ borderColor: '#E5E1DC', fontFamily: 'Georgia, serif' }} />
          <textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} placeholder="Notes" rows={2} className="w-full px-3 py-2 rounded-md border text-sm resize-none" style={{ borderColor: '#E5E1DC' }} />
          <div className="flex gap-2">
            <button type="submit" className="px-4 py-2 rounded-md text-sm font-medium text-white" style={{ backgroundColor: '#1B2A4A' }}>{editId ? 'Update' : 'Add IP'}</button>
            <button type="button" onClick={resetForm} className="px-4 py-2 rounded-md text-sm border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
          </div>
        </form>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden" style={{ borderColor: '#E5E1DC' }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr style={{ backgroundColor: '#F7F5F2' }}>
              <th className="text-left px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Mark</th>
              <th className="text-left px-3 py-2.5 text-xs font-medium hidden md:table-cell" style={{ color: '#6B7280' }}>App No.</th>
              <th className="text-left px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Status</th>
              <th className="text-left px-3 py-2.5 text-xs font-medium hidden md:table-cell" style={{ color: '#6B7280' }}>Class</th>
              <th className="text-left px-3 py-2.5 text-xs font-medium hidden sm:table-cell" style={{ color: '#6B7280' }}>Entity</th>
              <th className="text-left px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Valid Upto</th>
              <th className="text-left px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Renewal</th>
              <th className="text-right px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Actions</th>
            </tr></thead>
            <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
              {filtered.map(i => (
                <tr key={i.id} data-testid={`ip-row-${i.id}`}>
                  <td className="px-3 py-2.5">
                    <p className="font-medium" style={{ color: '#1B2A4A' }}>{i.mark_name}</p>
                    {i.story && <p className="text-xs truncate max-w-xs" style={{ color: '#9CA3AF', fontFamily: 'Georgia, serif' }}>{i.story}</p>}
                  </td>
                  <td className="px-3 py-2.5 text-xs font-mono hidden md:table-cell" style={{ color: '#6B7280' }}>{i.registration_number}</td>
                  <td className="px-3 py-2.5">
                    <span className="text-xs font-medium" style={{ color: statusColor(i.registration_status) }}>{i.registration_status || '—'}</span>
                  </td>
                  <td className="px-3 py-2.5 text-xs hidden md:table-cell" style={{ color: '#6B7280' }}>{i.class_info}</td>
                  <td className="px-3 py-2.5 text-xs hidden sm:table-cell" style={{ color: '#6B7280' }}>{i.entity}</td>
                  <td className="px-3 py-2.5 text-xs" style={{ color: '#1B2A4A' }}>
                    {i.expiry_date ? (
                      <span className="inline-flex items-center gap-1">
                        <Calendar size={10} style={{ color: '#9CA3AF' }} />
                        {new Date(i.expiry_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-2.5">
                    <span
                      className="text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                      style={{ backgroundColor: i.renewal.bg, color: i.renewal.fg }}
                      data-testid={`renewal-${i.id}`}
                    >
                      {i.renewal.tier === 'ok' ? <CheckCircle2 size={9} /> : <AlertTriangle size={9} />}
                      {i.renewal.label}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    <button onClick={async () => {
                      const years = window.prompt('Renewal term in years?', '10');
                      if (!years) return;
                      const notes = window.prompt('Receipt / notes (optional)', '') || '';
                      try {
                        const r = await api.post(`/ip-register/${i.id}/renew`, { years: parseInt(years, 10), notes });
                        alert(`Renewed → new expiry ${r.data.new_expiry}`);
                        refresh();
                      } catch (e) { alert(e?.response?.data?.detail || 'Renewal failed'); }
                    }} className="text-[10px] font-semibold px-2 py-1 rounded-md mr-1" style={{ backgroundColor: '#DCFCE7', color: '#166534' }} data-testid={`renew-ip-${i.id}`}>Renew</button>
                    <button onClick={() => handleEdit(i)} className="p-1 text-xs hover:bg-gray-100 rounded" style={{ color: '#2A7B88' }} data-testid={`edit-ip-${i.id}`}>Edit</button>
                    <button onClick={async () => { if (window.confirm('Delete?')) { await api.delete(`/ip-register/${i.id}`); refresh(); }}} className="p-1 hover:bg-gray-100 rounded" data-testid={`delete-ip-${i.id}`}><Trash2 size={13} style={{ color: '#D4726A' }} /></button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-sm" style={{ color: '#9CA3AF' }}>
                  {items.length === 0 ? 'No IP marks registered yet. Add manually or import via CSV.' : 'No marks match this filter.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatPill({ label, value, active, onClick, accent, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left rounded-md border px-3 py-2 transition-all hover:shadow-sm"
      style={{
        borderColor: active ? (accent || '#1B2A4A') : '#E5E1DC',
        backgroundColor: active ? ((accent || '#1B2A4A') + '10') : '#fff',
      }}
      data-testid={testId}
    >
      <p className="text-[10px] uppercase tracking-wide" style={{ color: '#6B7280' }}>{label}</p>
      <p className="text-lg font-bold" style={{ color: accent || '#1B2A4A' }}>{value}</p>
    </button>
  );
}
